import { useCustomerCtx, useDialogCtx } from 'src/hooks/context-hooks'
import { useTranslation } from 'react-i18next'
import { Box, useTheme, FormControlLabel, Checkbox } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { AppRoutesNavigation } from 'src/app-routes'
import { AddCircleOutline, Delete, Edit } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import GenericTable from 'src/components/table/GenericTable'
import { useGetInteractiveSchemas, useDeleteInteractiveSchema } from 'src/api/useInteractiveSchema'
import { useGetMainComponentTypeList} from '../../../api/main-component-type'
import { getTranslatedText } from 'src/utils'
import {useState} from 'react'

const InteractiveSchemas = () => {
    const { showConfirmDialog } = useDialogCtx()
    const { customer } = useCustomerCtx()
    const customerId = customer?.id
    const [showInactive, setShowInactive] = useState(false)
    const { data, isLoading } = useGetInteractiveSchemas(customerId!, !showInactive)
    const { data: equipmentTypesData } = useGetMainComponentTypeList(customer?.id as EntityId)
    const deleteMutation = useDeleteInteractiveSchema()
    const navigate = useNavigate()
    const theme = useTheme()
    const { t: translate } = useTranslation(['common', 'interactive-schema'])

    const equipmentTypes = equipmentTypesData || []

    const STATIC_COLUMNS = [
        {
            field: 'name',
            headerName: translate('name', { ns: 'common' }),
            flex: 3,
        },
        {
            field: 'description',
            headerName: translate('description', { ns: 'common' }),
            flex: 4,
            renderCell: params => params.row.description || '-',
        },
        {
            field: 'supportedEquipmentTypes',
            headerName: translate('equipment-types', { ns: 'interactive-schema' }),
            flex: 3,
            renderCell: params => {
                try {
                    let typeIds = params.row.supportedEquipmentTypeIds

                    if (typeof typeIds === 'string') {
                        typeIds = JSON.parse(typeIds)
                    }

                    if (!Array.isArray(typeIds) || typeIds.length === 0) {
                        return translate('all', { ns: 'common' })
                    }

                    const names = typeIds
                        .map(id => {
                            const type = equipmentTypes.find(t => t.id === id)
                            return type ? getTranslatedText(type.name) : null
                        })
                        .filter(Boolean)

                    if (names.length === 0) return typeIds.join(', ')

                    const text = names.join(', ')
                    if (text.length > 50) {
                        return text.substring(0, 47) + '...'
                    }
                    return text
                } catch (error) {
                    return '-'
                }
            },
        },
        {
            field: 'isActive',
            headerName: translate('active', { ns: 'common' }),
            flex: 1,
            renderCell: params => (params.row.isActive ? translate('yes', { ns: 'common' }) : translate('no', { ns: 'common' })),
        },
        {
            field: 'actions',
            headerName: translate('actions', { ns: 'common' }),
            flex: 2,
            renderCell: params => (
                <Box width={200} display="flex" flexDirection="row" justifyContent="space-evenly">
                    <Edit
                        className="cursor-pointer"
                        onClick={() => {
                            navigate(AppRoutesNavigation.Customer.editInteractiveSchema(customer?.id, params.row?.id))
                        }}
                    />
                    <Delete
                        className="cursor-pointer"
                        onClick={() => {
                            showConfirmDialog(
                                translate('delete-template-title', { ns: 'interactive-schema' }),
                                translate('delete-template-content', { ns: 'interactive-schema' }),
                                {
                                    acceptText: translate('yes', { ns: 'common' }),
                                    cancelText: translate('no', { ns: 'common' }),
                                },
                                async pop => {
                                    await deleteMutation.mutateAsync({ customerId: customerId!, id: params?.row?.id }, {
                                        onSuccess: () => {
                                            toast.success(translate('template-deleted', { ns: 'interactive-schema' }))
                                            pop()
                                        },
                                        onError: () => {
                                            toast.error(translate('error-deleting-template', { ns: 'interactive-schema' }))
                                        },
                                    })
                                },
                            )
                        }}
                    />
                </Box>
            ),
        },
    ]

    return (
        <CommonPageLayout
            titleSection={{ title: translate('activity-templates', { ns: 'interactive-schema' }) }}
            topAction={[
                {
                    buttonText: translate('new-template', { ns: 'interactive-schema' }),
                    buttonIcon: <AddCircleOutline />,
                    onClick: () => {
                        navigate(AppRoutesNavigation.Customer.createInteractiveSchema(customer?.id))
                    },
                    variant: 'outlined',
                    shadowWeight: null,
                    border: 0,
                },
            ]}
        >
            <Box padding="8px" bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius="4px">
                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showInactive}
                                onChange={(e) => setShowInactive(e.target.checked)}
                            />
                        }
                        label={String(translate('show-inactive-templates', { ns: 'interactive-schema' }))}
                    />
                </Box>
                <GenericTable
                    id="interactive-schemas"
                    columns={STATIC_COLUMNS}
                    data={data || []}
                    autoHeight={false}
                    height="81vh"
                    loading={isLoading}
                />
            </Box>
        </CommonPageLayout>
    )
}

export default InteractiveSchemas